// src/pages/BooksPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { BookmarkIcon, ArrowUpDown, Search, ChevronLeft, ChevronRight, User, Calendar, Grid, Book, Scroll, FileText, Heart, X, Clock, Trash2 } from "lucide-react";

import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";
import BookCard from "../components/BookCard";
import { fetchItemsLite, thumbUrlOf } from "../lib/omekaClient";
import { titleOf } from "../lib/textOf";

const ITEMS_PER_PAGE = 12;

const VIEW_DEFS = {
  all: { slug: "all", mode: "all", titleKey: "books.allTitle", fallbackTitle: "คลังข้อมูลดิจิตอล", icon: <Grid size={18} /> },
  palmleaf: { slug: "palmleaf", mode: "set", setId: "55", titleKey: "books.palmleafTitle", fallbackTitle: "คัมภีร์ใบลาน", icon: <Scroll size={18} /> },
  samutthai: { slug: "samutthai", mode: "set", setId: "56", titleKey: "books.samutthaiTitle", fallbackTitle: "หนังสือสมุดไทย", icon: <FileText size={18} /> },
  rare: { slug: "rare", mode: "set", setId: "54", titleKey: "books.rareTitle", fallbackTitle: "หนังสือหายาก", icon: <Book size={18} /> },
  bookmarks: { slug: "bookmarks", mode: "local", titleKey: "books.bookmarksTitle", fallbackTitle: "รายการที่บันทึกไว้", icon: <Heart size={18} /> },
  recent: { slug: "recent", mode: "local_recent", titleKey: "books.recentTitle", fallbackTitle: "ประวัติการเข้าชม", icon: <Clock size={18} /> },
};

const pickLang = (field, lang) => {
  if (!Array.isArray(field)) return "";
  const L = (lang || "").toLowerCase();
  const hit = field.find(x => (x?.["@language"] || "").toLowerCase() === L);
  return hit?.["@value"] || field[0]?.["@value"] || "";
};

export default function BooksPage() {
  const { t, i18n } = useTranslation();
  const { view: viewParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // 1. จัดการ State จาก URL และ Params
  const viewSlug = useMemo(() => (!viewParam || viewParam === "all" ? "all" : viewParam.toLowerCase()), [viewParam]);
  const currentDef = VIEW_DEFS[viewSlug] || VIEW_DEFS.all;

  const currentPage = parseInt(searchParams.get("page") || "1");
  const currentSort = searchParams.get("sort") || "newest";
  const currentQuery = searchParams.get("q") || "";

  // 2. Local State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inputQuery, setInputQuery] = useState(currentQuery);

  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarkData, setBookmarkData] = useState([]);
  const [recentData, setRecentData] = useState([]); // State สำหรับเก็บประวัติเข้าชม

  // --- Sync: เมื่อ URL เปลี่ยน ให้เปลี่ยนค่าใน Input ด้วย ---
  useEffect(() => {
    setInputQuery(currentQuery);
  }, [currentQuery]);

  // --- Logic: Debounce Search ---
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputQuery !== currentQuery) {
        setSearchParams(prev => {
          if (inputQuery) prev.set("q", inputQuery);
          else prev.delete("q");

          prev.set("page", "1");
          return prev;
        });
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [inputQuery, currentQuery, setSearchParams]);

  // --- Handler: เปลี่ยน Tab หมวดหมู่ ---
  const handleTabChange = (slug) => {
    setItems([]);
    setInputQuery("");
    const path = slug === "all" ? "/books" : `/books/${slug}`;
    navigate(path + "?page=1&sort=newest");
  };

  // --- Handler: เปลี่ยนหน้า ---
  const handlePageChange = (newPage) => {
    setSearchParams(prev => {
      prev.set("page", newPage);
      return prev;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Handler: เปลี่ยนการเรียงลำดับ ---
  const handleSortChange = (newSort) => {
    setItems([]);
    setSearchParams(prev => {
      prev.set("sort", newSort);
      prev.set("page", "1");
      return prev;
    });
  };

  // --- Handler: เคลียร์คำค้นหา ---
  const clearSearch = () => {
    setInputQuery("");
  };

  // --- Effect: โหลดข้อมูล Local (Bookmarks & Recent) ---
  useEffect(() => {
    const savedBookmarks = JSON.parse(localStorage.getItem("my_bookmarks") || "[]");
    setBookmarks(savedBookmarks.map(b => b.id));
    setBookmarkData(savedBookmarks);

    const savedRecent = JSON.parse(localStorage.getItem("recent_books") || "[]");
    setRecentData(savedRecent);
  }, [viewSlug]);

  // --- Effect: Main Data Fetching (ดึงข้อมูล) ---
  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);

      // กรณี: ดูรายการที่บันทึกไว้
      if (currentDef.mode === "local") {
        const saved = JSON.parse(localStorage.getItem("my_bookmarks") || "[]");
        if (isMounted) {
          let filtered = saved;
          if (currentQuery) {
            const lowerQ = currentQuery.toLowerCase();
            filtered = saved.filter(b =>
              (b.title || "").toLowerCase().includes(lowerQ) ||
              (b.creator || "").toLowerCase().includes(lowerQ)
            );
          }
          setItems(filtered);
          setLoading(false);
        }
      }
      // กรณี: ดูประวัติการเข้าชม
      else if (currentDef.mode === "local_recent") {
        const savedRecent = JSON.parse(localStorage.getItem("recent_books") || "[]");
        if (isMounted) {
          let filtered = savedRecent;
          if (currentQuery) {
            const lowerQ = currentQuery.toLowerCase();
            filtered = savedRecent.filter(b =>
              (b.title || "").toLowerCase().includes(lowerQ) ||
              (b.creator || "").toLowerCase().includes(lowerQ)
            );
          }
          setItems(filtered);
          setLoading(false);
        }
      }
      // กรณี: ดึงจาก Server (API)
      else {
        try {
          const params = {
            page: currentPage,
            limit: ITEMS_PER_PAGE,
            query: currentQuery
          };

          if (currentDef.mode === "set") params.item_set_id = currentDef.setId;

          switch (currentSort) {
            case "az":
              params.sortBy = "title";
              params.sortOrder = "asc";
              break;
            case "oldest":
              params.sortBy = "created";
              params.sortOrder = "asc";
              break;
            case "year_desc":
              params.sortBy = "year";
              params.sortOrder = "desc";
              break;
            case "year_asc":
              params.sortBy = "year";
              params.sortOrder = "asc";
              break;
            case "newest":
            default:
              params.sortBy = "created";
              params.sortOrder = "desc";
              break;
          }

          const data = await fetchItemsLite(params);

          if (isMounted) {
            setItems(Array.isArray(data) ? data : []);
            if (currentPage > 1) window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } catch (e) {
          console.error(e);
        } finally {
          if (isMounted) setLoading(false);
        }
      }
    })();
    return () => { isMounted = false; };
  }, [currentPage, viewSlug, currentSort, currentQuery, currentDef]);

  // --- Function: บันทึกประวัติ และเปิดอ่าน ---
  // --- Function: บันทึกประวัติ และไปหน้ารายละเอียดหนังสือ ---
  const handleBookClick = async (item) => {
    const id = item["o:id"] || item.id;
    if (!id) return;

    let recent = JSON.parse(localStorage.getItem("recent_books") || "[]");

    // เอาตัวที่ซ้ำออกก่อน เพื่อดันรายการล่าสุดไปอยู่บนสุด
    recent = recent.filter(b => b.id !== id);

    // ดึงหน้าปกมาเก็บไว้
    let thumbUrl = item.cover;
    if (!thumbUrl) {
      thumbUrl = await thumbUrlOf(item).catch(() => null) || "/assets/placeholder.jpg";
    }

    recent.unshift({
      id,
      title: pickLang(item["dcterms:title"], i18n.language) || item.title || titleOf(item),
      creator: pickLang(item["dcterms:creator"], i18n.language) || item.creator || t("books.unknownCreator"),
      date: pickLang(item["dcterms:date"], i18n.language) || item.date || t("books.unknownDate"),
      cover: thumbUrl
    });

    // เก็บแค่ 20 รายการล่าสุด
    recent = recent.slice(0, 20);
    localStorage.setItem("recent_books", JSON.stringify(recent));

    // ✅ เปลี่ยนจากหน้าอ่าน e-book ไปหน้ารายละเอียดก่อน
    navigate(`/book/${id}`);
  };

  // --- Function: Toggle Bookmark ---
  const toggleBookmark = async (item) => {
    let saved = JSON.parse(localStorage.getItem("my_bookmarks") || "[]");
    const id = item["o:id"] || item.id;
    const isExist = saved.find(b => b.id === id);

    if (isExist) {
      saved = saved.filter(b => b.id !== id);
    } else {
      const thumbUrl = await thumbUrlOf(item) || item.cover || "/assets/placeholder.jpg";
      saved.unshift({
        id,
        title: pickLang(item["dcterms:title"], i18n.language) || item.title || titleOf(item),
        creator: pickLang(item["dcterms:creator"], i18n.language) || item.creator || t("books.unknownCreator"),
        date: pickLang(item["dcterms:date"], i18n.language) || item.date || t("books.unknownDate"),
        cover: thumbUrl,
        rights: pickLang(item["dcterms:rights"], i18n.language) || item.rights || "",
        type: pickLang(item["dcterms:type"], i18n.language) || item.type || "",
        description: pickLang(item["dcterms:description"], i18n.language) || item.description || "",
        format: pickLang(item["dcterms:format"], i18n.language) || item.format || "",
        language: pickLang(item["dcterms:language"], i18n.language) || item.language || "",
        publisher: pickLang(item["dcterms:publisher"], i18n.language) || item.publisher || "",
        alternative: pickLang(item["dcterms:alternative"], i18n.language) || item.alternative || "",
        contributor: pickLang(item["dcterms:contributor"], i18n.language) || item.contributor || "",
        identifier: pickLang(item["dcterms:identifier"], i18n.language) || item.identifier || "",
        subject: pickLang(item["dcterms:subject"], i18n.language) || item.subject || "",
        source: pickLang(item["dcterms:source"], i18n.language) || item.source || "",
        relation: pickLang(item["dcterms:relation"], i18n.language) || item.relation || "",
        coverage: pickLang(item["dcterms:coverage"], i18n.language) || item.coverage || ""
      });
    }
    localStorage.setItem("my_bookmarks", JSON.stringify(saved));
    setBookmarks(saved.map(b => b.id));
    setBookmarkData(saved);

    if (currentDef.mode === "local") {
      if (currentQuery) {
        const lowerQ = currentQuery.toLowerCase();
        setItems(saved.filter(b => (b.title || "").toLowerCase().includes(lowerQ)));
      } else {
        setItems(saved);
      }
    }
  };

  // --- Function: Clear All Bookmarks ---
  const handleClearBookmarks = () => {
    if (window.confirm(t("books.clearBookmarksConfirm"))) {
      localStorage.setItem("my_bookmarks", "[]");
      setBookmarks([]);
      setBookmarkData([]);
      if (currentDef.mode === "local") {
        setItems([]);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f2] text-[#111518]">
      <Helmet>
        <title>{`${t(currentDef.titleKey, currentDef.fallbackTitle)} | ${t("books.pageSuffix")}`}</title>
      </Helmet>
      <SiteHeader />

      {/* Banner Section */}
      <section className="relative h-[25vh] md:h-[30vh] flex items-center justify-center bg-[#5b4a3e]">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url(/assets/banner.webp)" }} />
        <div className="relative z-10 text-center px-4 w-full max-w-2xl">
          <motion.h1 key={viewSlug} initial={{ y: 5, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-2xl md:text-4xl lg:text-5xl font-black text-white mb-6">
            {t(currentDef.titleKey, currentDef.fallbackTitle)}
          </motion.h1>

          {/* Search Box */}
          <div className="relative flex items-center">
            <Search className="absolute left-4 text-white/40" size={20} />
            <input
              type="text"
              placeholder={t("books.searchPlaceholder")}
              value={inputQuery}
              onChange={(e) => setInputQuery(e.target.value)}
              className="w-full pl-12 pr-32 py-3 md:py-4 rounded-2xl bg-white/10 border border-white/20 text-white placeholder:text-white/40 backdrop-blur-md outline-none focus:bg-white/20 transition-all shadow-xl"
            />
            <div className="absolute right-1.5 flex items-center gap-1">
              {inputQuery && (
                <button onClick={clearSearch} className="p-2 rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-colors">
                  <X size={18} />
                </button>
              )}
              <button
                onClick={() => {
                  setSearchParams(prev => {
                    if (inputQuery) prev.set("q", inputQuery);
                    else prev.delete("q");
                    prev.set("page", "1");
                    return prev;
                  });
                }}
                className="px-4 py-2 md:py-2.5 rounded-xl bg-white text-[#5b4a3e] text-sm font-bold hover:bg-orange-500 hover:text-white transition-all shadow-sm active:scale-95"
              >
                {t("search.button")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-4 gap-8 md:gap-12">
        {/* Sidebar */}
        <aside className="lg:col-span-1 space-y-6">

          {/* Box 1: หมวดหมู่ห้องสมุดหลัก */}
          <div className="bg-white p-2 rounded-[2rem] shadow-sm border border-[#5b4a3e]/5">
            <nav className="flex flex-col space-y-1">
              {Object.values(VIEW_DEFS)
                .filter(def => !["local", "local_recent"].includes(def.mode)) // กรอง Personal data ออก
                .map((def) => {
                  const isActive = viewSlug === def.slug;
                  return (
                    <button key={def.slug} onClick={() => handleTabChange(def.slug)} className={`flex items-center gap-3 px-6 py-4 rounded-2xl text-sm font-bold transition-all ${isActive ? "bg-[#5b4a3e] text-white shadow-lg shadow-[#5b4a3e]/20" : "text-[#5b4a3e] hover:bg-[#5b4a3e]/5"}`}>
                      {def.icon} {t(def.titleKey, def.fallbackTitle)}
                    </button>
                  );
                })}
            </nav>
          </div>

          {/* Box 2: ห้องสมุดส่วนตัว (Personal Space) */}
          <div className="bg-white p-2 rounded-[2rem] shadow-sm border border-[#5b4a3e]/5 overflow-hidden">
            <div className="px-6 py-5 flex items-center gap-3 border-b border-[#5b4a3e]/5 mb-2">
              <div className="w-1.5 h-5 bg-orange-500 rounded-full" /> {/* เส้นขีดส้มดึงสายตา */}
              <span className="text-[15px] font-black text-[#5b4a3e] tracking-tight">
                {t("books.personalLibrary")}
              </span>
            </div>

            <nav className="flex flex-col space-y-1">
              {/* รายการที่บันทึกไว้ */}
              <button
                onClick={() => handleTabChange(VIEW_DEFS.bookmarks.slug)}
                className={`flex items-center gap-3 px-6 py-4 rounded-2xl text-sm font-bold transition-all ${viewSlug === "bookmarks"
                  ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                  : "text-[#5b4a3e] hover:bg-orange-500/5"
                  }`}
              >
                <Heart size={18} fill={viewSlug === "bookmarks" ? "white" : "none"} />
                {t(VIEW_DEFS.bookmarks.titleKey, VIEW_DEFS.bookmarks.fallbackTitle)}
                {bookmarkData.length > 0 && (
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] ${viewSlug === "bookmarks" ? "bg-white text-orange-500" : "bg-[#5b4a3e] text-white"
                    }`}>
                    {bookmarkData.length}
                  </span>
                )}
              </button>

              {/* ประวัติการเข้าชม */}
              <button
                onClick={() => handleTabChange(VIEW_DEFS.recent.slug)}
                className={`flex items-center gap-3 px-6 py-4 rounded-2xl text-sm font-bold transition-all ${viewSlug === "recent"
                  ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20"
                  : "text-[#5b4a3e] hover:bg-[#5b4a3e]/5"
                  }`}
              >
                <Clock size={18} />
                {t(VIEW_DEFS.recent.titleKey, VIEW_DEFS.recent.fallbackTitle)}
                {recentData.length > 0 && (
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] ${viewSlug === "recent" ? "bg-white text-orange-500" : "bg-gray-100 text-gray-400"
                    }`}>
                    {recentData.length}
                  </span>
                )}
              </button>
            </nav>

            {/* Quick Actions (แสดงเฉพาะตอนอยู่หน้า Bookmarks และมีรายการอยู่) */}
            {viewSlug === "bookmarks" && bookmarkData.length > 0 && (
              <div className="mt-2 p-2 border-t border-[#5b4a3e]/5">
                <button
                  onClick={handleClearBookmarks}
                  className="w-full flex items-center justify-center gap-2 py-3 text-[12px] font-bold text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                >
                  <Trash2 size={14} />
                  {t("books.clearAll")}
                </button>
              </div>
            )}
          </div>

          {/* Sort Dropdown (ซ่อนสำหรับ Local Data) */}
          {!["local", "local_recent"].includes(currentDef.mode) && (
            <div className="bg-[#5b4a3e] p-6 rounded-[2rem] text-white shadow-xl">
              <div className="flex items-center gap-2 mb-4 text-[10px] font-bold uppercase tracking-[0.2em] opacity-50">
                <ArrowUpDown size={12} /> {t("books.sortLabel")}
              </div>
              <select
                value={currentSort}
                onChange={(e) => handleSortChange(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl p-3 text-sm outline-none cursor-pointer"
              >
                <option value="newest" className="text-black">{t("books.sortNewest")}</option>
                <option value="oldest" className="text-black">{t("books.sortOldest")}</option>
                <option value="year_desc" className="text-black">{t("books.sortYearDesc")}</option>
                <option value="year_asc" className="text-black">{t("books.sortYearAsc")}</option>
                <option value="az" className="text-black">{t("books.sortAz")}</option>
              </select>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {loading ? (
              <div key="loading" className="grid grid-cols-2 md:grid-cols-3 gap-8">{[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-[#e5d7ca]/30 animate-pulse rounded-[2rem]" />)}</div>
            ) : (
              <motion.div key={viewSlug + currentQuery + currentSort + currentPage} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-12">

                {/* Empty State */}
                {items.length === 0 ? (
                  <div className="py-24 text-center text-[#5b4a3e]/30 font-bold italic">
                    {currentQuery
                      ? t("books.emptySearch", { query: currentQuery })
                      : (currentDef.mode === "local"
                        ? t("books.emptyBookmarks")
                        : (currentDef.mode === "local_recent"
                          ? t("books.emptyRecent")
                          : t("books.emptyResources")))
                    }
                  </div>
                ) : (
                  // Grid Items
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                    {items.map((it) => {
                      const id = it.id || it["o:id"];
                      return (
                        <div key={id} className="flex flex-col group">
                          <div className="relative mb-4 aspect-[3/4] rounded-[2rem] overflow-hidden bg-white shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                            {/* เปลี่ยน onOpen ไปเรียก handleBookClick แทนเพื่อบันทึกประวัติก่อนไปหน้าอ่าน */}
                            <BookCard item={it} onOpen={() => handleBookClick(it)} />
                            <button
                              onClick={() => toggleBookmark(it)}
                              className={`absolute top-4 right-4 p-2.5 rounded-full shadow-lg transition-all z-30 ${ // <-- เพิ่ม z-30 ตรงนี้
                                bookmarks.includes(id)
                                  ? "bg-orange-500 text-white scale-110 opacity-100" // ให้คงความชัดไว้เสมอถ้าบันทึกแล้ว
                                  : "bg-white/90 text-gray-700 opacity-0 group-hover:opacity-100"
                                }`}
                            >
                              <BookmarkIcon size={16} fill={bookmarks.includes(id) ? "currentColor" : "none"} />
                            </button>
                          </div>
                          <div className="px-2">
                            <h4 className="font-bold text-[#5b4a3e] text-sm md:text-base line-clamp-1 mb-1">{it.title || pickLang(it["dcterms:title"], i18n.language) || titleOf(it)}</h4>
                            <div className="space-y-0.5 opacity-60">
                              <p className="flex items-center gap-1.5 text-[10px] md:text-xs font-medium">
                                <User size={12} /> {it.creator || pickLang(it["dcterms:creator"], i18n.language) || t("books.unknownCreator")}
                              </p>

                              <p className="flex items-center gap-1.5 text-[10px] md:text-xs font-medium">
                                <Calendar size={12} /> {it.date || pickLang(it["dcterms:date"], i18n.language) || t("books.unknownDate")}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pagination */}
                {!["local", "local_recent"].includes(currentDef.mode) && items.length > 0 && (
                  <div className="flex items-center justify-center gap-8 py-12 border-t border-[#5b4a3e]/10">
                    <button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} className="p-3 rounded-2xl bg-white border border-[#5b4a3e]/10 disabled:opacity-20 hover:bg-[#5b4a3e] hover:text-white transition-all"><ChevronLeft size={24} /></button>
                    <span className="text-xl font-black text-[#5b4a3e]">{currentPage}</span>
                    <button disabled={items.length < ITEMS_PER_PAGE} onClick={() => handlePageChange(currentPage + 1)} className="p-3 rounded-2xl bg-white border border-[#5b4a3e]/10 disabled:opacity-20 hover:bg-[#5b4a3e] hover:text-white transition-all"><ChevronRight size={24} /></button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
      <BackToTop />
      <Footer />
    </div>
  );
}